import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useNavigate } from "react-router-dom";


interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface VisibilityState {
  graphic: boolean;
  content: boolean;
  badges: boolean;
  countdown: boolean;
}

const About = () => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 21,
    hours: 7,
    minutes: 42,
    seconds: 0
  });
  
  const [isVisible, setIsVisible] = useState<VisibilityState>({
    graphic: false,
    content: false,
    badges: false,
    countdown: false
  });

  const graphicRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const countdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Parse the target date with explicit date parsing
    const targetDate = new Date('2026-01-24T00:00:00').getTime();
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        // Countdown reached zero
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const observerOptions: IntersectionObserverInit = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const targetName = (entry.target as HTMLElement).dataset.animate;
          if (targetName && targetName in isVisible) {
            setIsVisible(prev => ({ 
              ...prev, 
              [targetName as keyof VisibilityState]: true 
            }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (graphicRef.current) observer.observe(graphicRef.current);
    if (contentRef.current) observer.observe(contentRef.current);
    if (badgesRef.current) observer.observe(badgesRef.current);
    if (countdownRef.current) observer.observe(countdownRef.current);

    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Main Content */}

      <button
  disabled
  className="mt-4 px-8 py-3 
             bg-gradient-to-r from-red-900 to-red-900 
             text-white font-bold rounded-full 
             opacity-50 cursor-not-allowed"
>
  Register Now
</button>

      <div className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          {/* Left Side - 3D Graphic */}
          <div 
            ref={graphicRef}
            data-animate="graphic"
            className={`flex justify-center transition-all duration-1000 ${
              isVisible.graphic 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 -translate-x-20'
            }`}
          >
            <div className="relative w-96 h-96">
              <svg viewBox="0 0 400 400" className="w-full h-full animate-pulse">
                {/* Outer wireframe structure */}
                <g className="opacity-60">
                  <polygon points="200,50 320,120 320,280 200,350 80,280 80,120" 
                    fill="none" stroke="#ff0000" strokeWidth="2"/>
                  <polygon points="200,80 290,130 290,270 200,320 110,270 110,130" 
                    fill="none" stroke="#ff3333" strokeWidth="1.5"/>
                </g>
                
                {/* Inner core */}
                <g className="opacity-80">
                  <circle cx="200" cy="200" r="80" fill="url(#redGradient)" opacity="0.3"/>
                  <circle cx="200" cy="200" r="60" fill="none" stroke="#ff0000" strokeWidth="2"/>
                  <circle cx="200" cy="200" r="40" fill="none" stroke="#ff6666" strokeWidth="1.5"/>
                </g>
                
                {/* Connecting lines */}
                <g className="opacity-40">
                  <line x1="200" y1="50" x2="200" y2="350" stroke="#ff0000" strokeWidth="1"/>
                  <line x1="80" y1="120" x2="320" y2="280" stroke="#ff0000" strokeWidth="1"/>
                  <line x1="320" y1="120" x2="80" y2="280" stroke="#ff0000" strokeWidth="1"/>
                </g>
                
                {/* Gradient definition */}
                <defs>
                  <radialGradient id="redGradient">
                    <stop offset="0%" stopColor="#ff0000" stopOpacity="0.8"/>
                    <stop offset="100%" stopColor="#660000" stopOpacity="0.2"/>
                  </radialGradient>
                </defs>
              </svg>
            </div>
          </div>

          {/* Right Side - Content */}
          <div 
            ref={contentRef}
            data-animate="content"
            className={`space-y-6 transition-all duration-1000 delay-300 ${
              isVisible.content 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 translate-x-20'
            }`}
          >
            <h1 className="text-5xl font-bold">
              About <span className="text-red-600">The GRID Hackathon</span>
            </h1>
            
            <p className="text-gray-300 leading-relaxed text-justify">
              Astranova Hackathon is a student-driven AI innovation event focused on ethical problem-solving.
              It challenges participants to design intelligent solutions to combat deceptive practices in e-commerce platforms.
              The hackathon provides a collaborative space for {' '}
              <span className="text-red-600 font-semibold">ideation, creativity, and hands-on development</span>.
              Participants work in teams to build AI-powered prototypes with real-world relevance.
              The event emphasizes responsible, transparent, and user-centric use of artificial intelligence.
              Students gain exposure to practical problem-solving and interdisciplinary collaboration.
              Solutions are evaluated by mentors and industry experts during the final showcase.
              The hackathon concludes with an on-campus event on .{' '}
              <span className="text-red-600 font-semibold">24 January 2026</span>
            </p>

            <div className="space-y-3 pt-4">
              <div 
                ref={badgesRef}
                data-animate="badges"
                className={`space-y-3 transition-all duration-700 delay-500 ${
                  isVisible.badges 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-10'
                }`}
              >
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="bg-red-900/30 p-2 rounded-lg border border-red-700/50">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                      <path strokeWidth="2" d="M12 6v6l4 2"/>
                    </svg>
                  </div>
                  <span>One Month Online + On-Campus Finale</span>
                </div>
                
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="bg-red-900/30 p-2 rounded-lg border border-red-700/50">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <span>Coimbatore Institute of Technology, Coimbatore</span>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <img 
                src="https://upload.wikimedia.org/wikipedia/en/thumb/6/6a/Amrita_Vishwa_Vidyapeetham_Logo.svg/1200px-Amrita_Vishwa_Vidyapeetham_Logo.svg.png" 
                alt="Amrita Logo" 
                className="h-12 brightness-0 invert opacity-80"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>

        {/* Countdown Timer */}
        <div 
          ref={countdownRef}
          data-animate="countdown"
          className={`bg-gradient-to-br from-red-950/30 to-black border border-red-900/50 rounded-2xl p-12 backdrop-blur-sm transition-all duration-1000 delay-700 ${
            isVisible.countdown 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-20'
          }`}
        >
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            Grand Finale In
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {Object.entries(timeLeft).map(([unit, value]) => (
              <div key={unit} className="bg-black/60 border border-red-800/50 rounded-xl p-8 text-center backdrop-blur-sm hover:border-red-600/70 transition-all duration-300">
                <div className="text-6xl font-bold text-red-500 mb-3">
                  {String(value).padStart(2, '0')}
                </div>
                <div className="text-gray-400 text-lg capitalize">{unit}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;